import { useEffect } from "react";

/** Konfetti außerhalb von `<main>` – portiert aus `static/partials/header.js`. */
export function useBirthdayConfetti(isActive: boolean): void {
  useEffect(() => {
    if (typeof document === "undefined") return;

    let clipRaf = 0;
    let resizeObserver: ResizeObserver | null = null;

    const disconnectObserver = () => {
      resizeObserver?.disconnect();
      resizeObserver = null;
    };

    const queryPrimaryMainEl = () =>
      document.querySelector("main.eh-layout-main") ||
      document.querySelector("main#main") ||
      document.querySelector("main.main-area") ||
      document.querySelector("main:not([hidden])");

    const updateClip = () => {
      const layer = document.getElementById("eh-birthday-confetti-layer");
      if (!layer || layer.hasAttribute("hidden")) return;

      const main = queryPrimaryMainEl();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (!main) {
        layer.style.clipPath = "none";
        return;
      }

      const rect = main.getBoundingClientRect();
      if (rect.width < 4 || rect.height < 4) {
        layer.style.clipPath = "none";
        return;
      }

      const pad = 6;
      const left = Math.max(0, rect.left - pad);
      const top = Math.max(0, rect.top - pad);
      const right = Math.min(vw, rect.right + pad);
      const bottom = Math.min(vh, rect.bottom + pad);

      layer.style.clipPath = [
        "polygon(evenodd,",
        `0px 0px, ${vw}px 0px, ${vw}px ${vh}px, 0px ${vh}px, 0px 0px,`,
        `${left}px ${top}px, ${right}px ${top}px, ${right}px ${bottom}px, ${left}px ${bottom}px, ${left}px ${top}px)`,
      ].join(" ");
    };

    const scheduleClip = () => {
      if (clipRaf) cancelAnimationFrame(clipRaf);
      clipRaf = requestAnimationFrame(() => {
        clipRaf = 0;
        updateClip();
      });
    };

    const bindMainObserver = () => {
      disconnectObserver();
      const main = queryPrimaryMainEl();
      if (!main) return;
      resizeObserver = new ResizeObserver(() => scheduleClip());
      resizeObserver.observe(main);
    };

    const layerId = "eh-birthday-confetti-layer";

    const teardown = () => {
      document.body.classList.remove("eh-my-birthday");
      const layer = document.getElementById(layerId);
      layer?.setAttribute("hidden", "");
      layer?.style.removeProperty("clip-path");
      disconnectObserver();
      window.removeEventListener("resize", scheduleClip);
      window.removeEventListener("scroll", scheduleClip, true);
      if (clipRaf) cancelAnimationFrame(clipRaf);
    };

    if (!isActive) {
      teardown();
      return teardown;
    }

    document.body.classList.add("eh-my-birthday");

    let layer = document.getElementById(layerId);
    if (!layer) {
      layer = document.createElement("div");
      layer.id = layerId;
      layer.setAttribute("aria-hidden", "true");
      const inner = document.createElement("div");
      inner.className = "eh-global-confetti";
      layer.appendChild(inner);
      const anchor = document.body.firstElementChild;
      if (anchor) document.body.insertBefore(layer, anchor);
      else document.body.appendChild(layer);
    }

    layer.removeAttribute("hidden");
    bindMainObserver();
    scheduleClip();

    window.addEventListener("resize", scheduleClip, { passive: true });
    window.addEventListener("scroll", scheduleClip, { passive: true, capture: true });

    return teardown;
  }, [isActive]);
}
